'use client'

import * as React from 'react'
import { Calendar as CalendarIcon, X } from 'lucide-react'
import { format, parseISO, isValid } from 'date-fns'
import { nl } from 'date-fns/locale'
import { cn } from '../lib/utils'
import { Button } from './button'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

export interface MultiDatePickerProps {
  /** Geselecteerde datums als 'yyyy-MM-dd' ISO strings */
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  fromYear?: number
  toYear?: number
  placeholder?: string
  className?: string
  id?: string
}

/** Converteer 'yyyy-MM-dd' ISO string naar lokale Date (geen UTC-middernacht-verschuiving). */
function isoToDate(iso: string): Date | undefined {
  const d = parseISO(iso)
  return isValid(d) ? d : undefined
}

/** Converteer Date naar 'yyyy-MM-dd' ISO string. */
function dateToIso(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

/** Sorteer ISO-datum-strings chronologisch en verwijder duplicaten. */
function sortedUnique(isos: string[]): string[] {
  return [...new Set(isos)].sort()
}

export function MultiDatePicker({
  value,
  onChange,
  disabled = false,
  fromYear,
  toYear,
  placeholder = 'Kies datums',
  className,
  id,
}: MultiDatePickerProps): JSX.Element {
  const [open, setOpen] = React.useState(false)

  // Converteer de string[]-waarden naar Date[] voor react-day-picker.
  const selectedDates = React.useMemo<Date[]>(
    () =>
      value
        .map(isoToDate)
        .filter((d): d is Date => d !== undefined),
    [value],
  )

  // Gebruik dropdown-knoppen voor jaar/maand als een jaarbereik is opgegeven,
  // identiek aan de bestaande DatePicker.
  const captionLayout =
    fromYear != null || toYear != null ? ('dropdown-buttons' as const) : undefined

  const dropdownDefaults =
    captionLayout === 'dropdown-buttons'
      ? {
          fromYear: fromYear ?? 1940,
          toYear: toYear ?? new Date().getFullYear() + 1,
        }
      : { fromYear, toYear }

  /** Verwerk het onSelect-event van react-day-picker mode="multiple". */
  function handleSelect(dates: Date[] | undefined): void {
    const nextIsos = sortedUnique((dates ?? []).map(dateToIso))
    onChange(nextIsos)
  }

  /** Verwijder een specifieke datum via het chip-kruisje. */
  function handleRemove(iso: string): void {
    onChange(sortedUnique(value.filter((v) => v !== iso)))
  }

  // Samenvatting voor de trigger-knop.
  const triggerLabel =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? (() => {
            const d = isoToDate(value[0])
            return d ? format(d, 'd MMM yyyy', { locale: nl }) : value[0]
          })()
        : `${value.length} datums gekozen`

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            aria-label={value.length === 0 ? placeholder : `${value.length} datums gekozen, kalender openen`}
            className={cn(
              'w-full justify-start text-left font-normal',
              value.length === 0 && 'text-[#5C5C58]',
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            <span className="truncate">{triggerLabel}</span>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          // De kalender mag nooit buiten het scherm vallen: in een modal is de
          // pagina erachter vergrendeld, dus wat onder de rand valt is
          // onbereikbaar. Begrens op de ruimte die Radix meet en scroll daarbinnen.
          className="w-auto max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-0"
          align="start"
          collisionPadding={8}
          // De scroll-vergrendeling van een omliggende Dialog blokkeert wiel/touch
          // in deze geportalde popover; laat die events hier niet doorbubbelen.
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <Calendar
            mode="multiple"
            selected={selectedDates}
            onSelect={handleSelect}
            captionLayout={captionLayout}
            fromYear={dropdownDefaults.fromYear}
            toYear={dropdownDefaults.toYear}
            initialFocus
          />
          {value.length > 0 && (
            <div className="border-t border-[#CED0C8] p-2 flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  onChange([])
                  setOpen(false)
                }}
              >
                Alles wissen
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Chips — geselecteerde datums onder de trigger */}
      {value.length > 0 && (
        <div
          className="flex flex-wrap gap-1"
          role="group"
          aria-label="Geselecteerde datums"
        >
          {value.map((iso) => {
            const d = isoToDate(iso)
            const label = d ? format(d, 'd MMM yyyy', { locale: nl }) : iso
            return (
              <span
                key={iso}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md border border-[#CED0C8]',
                  'bg-[#F0F1EC] px-2 py-0.5 text-sm text-[#1A1A18]',
                )}
              >
                {label}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => handleRemove(iso)}
                  aria-label={`Datum verwijderen: ${label}`}
                  className={cn(
                    'ml-0.5 rounded-sm opacity-70 hover:opacity-100 focus:outline-none',
                    'focus:ring-2 focus:ring-[#CCFA78] focus:ring-offset-1',
                    'disabled:pointer-events-none',
                  )}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}
